import React from "react";
import { MdAdd, MdLocalShipping, MdOutlineRoute } from "react-icons/md";
import { routeFilterOptions } from "../Cells/RouteDropdown";
import { formatCount } from "../../orderHeaderStats";

/**
 * The routes bar under the Orders toolbar, shown while the list is on
 * self-delivery orders: All, then one tab per route (its colour dot and
 * name); a tab sets the Route filter (`selected` is that filter's value,
 * "" for All). Each tab shows how many orders it has when known — a route's
 * orders_count, and `allCount` (self-delivery orders) on All. Add route and
 * Manage routes open the routes pop-up. On phones the tabs get a row of their
 * own, under Add route and Manage routes. With `onRuns` a Runs button opens
 * the account's delivery runs.
 */
const RoutesBar = ({
  routes = [],
  allCount = null,
  selected = "",
  onSelect,
  onAdd,
  onManage,
  onRuns,
}) => {
  const options = routeFilterOptions(routes);

  const tab = (value, label, dot = null, count = null) => {
    const active = (selected ?? "") === value;
    return (
      <button
        key={value || "__all"}
        type="button"
        aria-pressed={active}
        onClick={() => onSelect(value)}
        className={`flex h-[28px] shrink-0 items-center gap-[6px] whitespace-nowrap rounded-full border-[1px] px-[11px] text-xs transition-colors ${
          active
            ? "border-brand bg-ui-surface font-medium text-brand shadow-sm"
            : "border-transparent text-ui-text-muted hover:border-ui-border hover:bg-ui-surface hover:text-ui-text"
        }`}
      >
        {dot && (
          <span
            aria-hidden="true"
            className="h-[8px] w-[8px] shrink-0 rounded-full"
            style={{ backgroundColor: dot.color || "#9ca3af" }}
          />
        )}
        {label}
        {count != null && (
          <span
            className={`min-w-[18px] rounded-full px-[5px] text-center text-[10px] font-medium leading-[16px] ${
              active
                ? "bg-brand-light text-brand"
                : "bg-state-neutral-bg text-ui-text-muted"
            }`}
          >
            {formatCount(count)}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex min-h-[42px] items-center gap-x-[10px] gap-y-[6px] border-y-[1px] border-ui-border bg-ui-surface-sunken px-[14px] py-[6px] [flex-wrap:wrap] sm:[flex-wrap:nowrap]">
      <span className="hidden shrink-0 items-center gap-[6px] text-[10.5px] font-medium uppercase tracking-[0.04em] text-ui-text-muted sm:flex">
        <MdOutlineRoute aria-hidden="true" className="h-[14px] w-[14px]" />
        Routes
      </span>
      <div
        role="group"
        aria-label="Delivery routes"
        className="scrollbar-hide order-[99] flex min-w-0 basis-full items-center gap-[4px] overflow-x-auto sm:order-[0] sm:basis-auto"
      >
        {tab("", "All", null, allCount)}
        {options.map((option) =>
          tab(
            option.value,
            option.label,
            { color: option.color },
            option.count,
          ),
        )}
        {options.length === 0 && (
          <span className="shrink-0 whitespace-nowrap px-[6px] text-xs text-ui-text-subtle">
            No routes yet
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onAdd}
        aria-haspopup="dialog"
        className="flex h-[28px] shrink-0 items-center gap-[5px] whitespace-nowrap rounded-md px-[9px] text-xs font-medium text-brand transition-colors hover:bg-brand-light"
      >
        <MdAdd aria-hidden="true" className="h-[13px] w-[13px] shrink-0" />
        Add route
      </button>
      {onRuns && (
        <button
          type="button"
          onClick={onRuns}
          aria-haspopup="dialog"
          className="ml-auto flex shrink-0 items-center gap-[5px] whitespace-nowrap rounded-md border border-ui-border bg-ui-surface px-[11px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand"
        >
          <MdLocalShipping className="h-3 w-3 shrink-0" />
          Runs
        </button>
      )}
      <button
        type="button"
        onClick={onManage}
        aria-haspopup="dialog"
        className={`${onRuns ? "" : "ml-auto "}flex shrink-0 items-center gap-[5px] whitespace-nowrap rounded-md border border-ui-border bg-ui-surface px-[11px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand`}
      >
        <MdOutlineRoute className="h-3 w-3 shrink-0" />
        Manage routes
      </button>
    </div>
  );
};

export default RoutesBar;
